import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogApi, sitesApi, ApiError, uploadApi } from '../../api/client';
import type {
  CreateBlogPostRequest,
  UpdateBlogPostRequest,
  BlogPostStatus,
} from '../../schemas/validation';
import type { BlogPostEditorState } from './useBlogPostEditorState';

interface UseBlogPostEditorHandlersProps {
  siteId: string | undefined;
  postId: string | undefined;
  isNewPost: boolean;
  state: BlogPostEditorState;
}

export const useBlogPostEditorHandlers = ({
  siteId,
  postId,
  isNewPost,
  state,
}: UseBlogPostEditorHandlersProps) => {
  const navigate = useNavigate();

  const loadInitialData = useCallback(async () => {
    if (!siteId) {
      navigate('/dashboard');
      return;
    }

    try {
      state.setIsLoading(true);

      // Load site info
      const allSites = await sitesApi.list();
      const currentSite = allSites.find(s => s.id === siteId);

      if (!currentSite) {
        navigate('/dashboard');
        return;
      }

      state.setSite(currentSite);

      const blogData = await blogApi.get(siteId);
      state.setBlog(blogData.blog);

      // Load post data if editing existing post
      if (!isNewPost && postId) {
        try {
          const response = await blogApi.posts.get(siteId, postId);
          const postData = response.post;
          state.setPost(postData);
          state.setTitle(postData.title);
          state.setSlug(postData.slug);
          state.setContent(postData.content || '');
          state.setExcerpt(postData.excerpt || '');
          state.setFeaturedImageUrl(postData.featuredImageUrl || '');
          state.setFeaturedImageCrop(postData.featuredImageCrop);
          state.setAuthorName(postData.authorName || '');
          state.setTags(postData.tags || []);
          state.setSeoTitle(postData.seoTitle || '');
          state.setSeoDescription(postData.seoDescription || '');
          state.setSeoImageUrl(postData.seoImageUrl || '');
        } catch (error: any) {
          if (error?.status === 404) {
            state.showNotification('Post not found', 'error');
            navigate(`/sites/${siteId}/blog`);
            return;
          }
          throw error;
        }
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        navigate('/login');
      } else {
        console.error('Failed to load data:', error);
        navigate(`/sites/${siteId}/blog`);
      }
    } finally {
      state.setIsLoading(false);
    }
  }, [siteId, postId, isNewPost, navigate, state]);

  const handleSave = useCallback(
    async (publish: boolean = false) => {
      if (!siteId || !state.title.trim()) {
        state.showNotification('Title is required', 'error');
        return;
      }

      try {
        if (publish) {
          state.setIsPublishing(true);
        } else {
          state.setIsSaving(true);
        }

        const postData: CreateBlogPostRequest | UpdateBlogPostRequest = {
          title: state.title.trim(),
          slug: state.slug.trim() || undefined, // Send custom slug if provided
          content: state.content,
          excerpt: state.excerpt.trim() || undefined,
          featuredImageUrl: state.featuredImageUrl.trim() || undefined,
          featuredImageCrop: state.featuredImageCrop,
          authorName: state.authorName.trim() || undefined,
          tags: state.tags.length > 0 ? state.tags : undefined,
          seoTitle: state.seoTitle.trim() || undefined,
          seoDescription: state.seoDescription.trim() || undefined,
          seoImageUrl: state.seoImageUrl.trim() || undefined,
          status: publish ? 'published' : 'draft',
        };

        if (isNewPost) {
          const response = await blogApi.posts.create(siteId, postData as CreateBlogPostRequest);
          const savedPost = response.post;
          // Navigate to edit the newly created post
          navigate(`/sites/${siteId}/blog/posts/${savedPost.id}`, {
            replace: true,
          });
          state.setPost(savedPost);
          state.setSlug(savedPost.slug); // Update slug with the saved value
        } else {
          const response = await blogApi.posts.update(
            siteId,
            postId!,
            postData as UpdateBlogPostRequest
          );
          state.setPost(response.post);
          state.setSlug(response.post.slug); // Update slug with the saved value
        }

        state.setHasUnsavedChanges(false);
        state.showNotification(
          publish ? 'Post published successfully!' : 'Post saved successfully!',
          'success',
          publish
            ? { showRoary: true, phraseKey: 'blog_published' }
            : { showRoary: true, phraseKey: 'changes_saved' }
        );
      } catch (error: any) {
        console.error('Failed to save post:', error);
        state.showNotification(error?.message || 'Failed to save post', 'error');
      } finally {
        state.setIsSaving(false);
        state.setIsPublishing(false);
      }
    },
    [siteId, postId, isNewPost, navigate, state]
  );

  const handleDelete = useCallback(async () => {
    if (!siteId || !postId || isNewPost) return;

    try {
      state.setIsDeleting(true);
      await blogApi.posts.delete(siteId, postId);
      state.showNotification('Post deleted successfully', 'success');
      navigate(`/sites/${siteId}/blog`);
    } catch (error: any) {
      console.error('Failed to delete post:', error);
      state.showNotification(error?.message || 'Failed to delete post', 'error');
    } finally {
      state.setIsDeleting(false);
      state.setShowDeleteModal(false);
    }
  }, [siteId, postId, isNewPost, navigate, state]);

  const handleTogglePublish = useCallback(async () => {
    if (!siteId || !postId || isNewPost || !state.post) return;

    const newStatus: BlogPostStatus = state.post.status === 'published' ? 'draft' : 'published';

    try {
      state.setIsPublishing(true);
      const response = await blogApi.posts.setPublished(siteId, postId, newStatus);
      state.setPost(response.post);
      state.showNotification(
        newStatus === 'published' ? 'Post published!' : 'Post unpublished',
        'success',
        newStatus === 'published' ? { showRoary: true, phraseKey: 'blog_published' } : undefined
      );
    } catch (error: any) {
      console.error('Failed to toggle publish status:', error);
      state.showNotification(error?.message || 'Failed to update post status', 'error');
    } finally {
      state.setIsPublishing(false);
    }
  }, [siteId, postId, isNewPost, state]);

  const handleImageUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !siteId) return;

      try {
        state.setIsUploadingImage(true);
        const result = await uploadApi.uploadWithPresignedUrl(file, {
          siteId,
          componentType: 'blog',
          assetType: 'image',
        });
        state.setFeaturedImageUrl(result.cloudfrontUrl || result.url);
        state.setHasUnsavedChanges(true);
        state.showNotification('Image uploaded successfully', 'success', {
          showRoary: true,
          phraseKey: 'generic_success',
        });
      } catch (error: any) {
        console.error('Failed to upload image:', error);
        state.showNotification(error?.message || 'Failed to upload image', 'error');
      } finally {
        state.setIsUploadingImage(false);
      }
    },
    [siteId, state]
  );

  const handleContentImageUpload = useCallback(
    async (file: File): Promise<string> => {
      if (!siteId) throw new Error('Site ID is required');

      const result = await uploadApi.uploadWithPresignedUrl(file, {
        siteId,
        componentType: 'blog',
        assetType: 'image',
      });
      return result.cloudfrontUrl || result.url;
    },
    [siteId]
  );

  const handleAddTag = useCallback(() => {
    const trimmedTag = state.newTag.trim().toLowerCase();
    if (trimmedTag && !state.tags.includes(trimmedTag)) {
      state.setTags([...state.tags, trimmedTag]);
      state.setNewTag('');
      state.setHasUnsavedChanges(true);
    }
  }, [state]);

  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      state.setTags(state.tags.filter(t => t !== tagToRemove));
      state.setHasUnsavedChanges(true);
    },
    [state]
  );

  const handleRemoveFeaturedImage = useCallback(() => {
    state.setFeaturedImageUrl('');
    state.setFeaturedImageCrop(undefined);
    state.setHasUnsavedChanges(true);
  }, [state]);

  return {
    loadInitialData,
    handleSave,
    handleDelete,
    handleTogglePublish,
    handleImageUpload,
    handleContentImageUpload,
    handleAddTag,
    handleRemoveTag,
    handleRemoveFeaturedImage,
  };
};
